// Sign-in is by a code sent by email, Apple or Google. Password sign-in, and the verification
// links that came with it, were retired on 2026-09-26 (backend answers 410 on those routes).

// What /auth/verify-code answered. It says whether a name and barrio are still needed
// (needs_profile), for a new address or for an unverified account its owner is taking over.
// /auth/magic-code no longer says whether an address has an account, so this is the only signal.
export function magicVerifyOutcome(data) {
  if (data?.token) return 'signed_in';
  if (data?.status === 'needs_profile') return 'needs_profile';
  return 'error';
}

// Query parameters from links in emails sent before the retirement: a password reset link
// (/?reset_token=…) and the old verification redirect (/?verification=…).
export const LEGACY_AUTH_PARAMS = ['reset_token', 'verification'];

export const LEGACY_AUTH_NOTICE =
  'Ya no hace falta contraseña. Escribe tu email y te enviaremos un código para entrar: tu cuenta y tus datos siguen ahí.';

export function hasLegacyAuthParams(searchParams) {
  return LEGACY_AUTH_PARAMS.some((p) => searchParams.has(p));
}

// ── Accounts are for adults (18+) ───────────────────────────────────────────────────────────
// Reading the app needs no account. Creating one needs «Confirmo que tengo 18 años o más»,
// ticked by the person (never pre-ticked), whichever way they sign in. The backend refuses to
// create an account without it: 400 with code age_confirmation_required. Existing accounts are
// never asked (they confirmed when they registered).
export const AGE_REQUIRED_CODE = 'age_confirmation_required';
export const AGE_CONFIRM_LABEL = 'Confirmo que tengo 18 años o más';
export const PRIVACY_POLICY_PATH = '/legal?tab=privacidad';

// The backend's answer to an Apple or Google sign-in that would create an account without the
// confirmation. The same token can be sent again, with age_confirmed, once the box is ticked.
export function isAgeRequired(err) {
  return err?.response?.data?.code === AGE_REQUIRED_CODE;
}

export function withAgeConfirmation(payload) {
  return { ...(payload || {}), age_confirmed: true };
}


// ── Native Apple / Google sign-in errors ─────────────────────────────────────────────────────
// The app shell answers a failed native sign-in with { error: '<text>' } and the page shows the
// text. Shells up to 1.9.8 sent debug text for Google — "Token no recibido. Debug: {…}" and
// "Error: <code> - <English message>" — and "Error con Apple Sign In" for Apple. Those are
// replaced here, so every installed shell shows plain Spanish; anything else the shell says is
// kept (1.9.9 already sends Spanish).
export const SOCIAL_SIGN_IN_FAILED = {
  google: 'No se ha podido iniciar sesión con Google. Inténtalo de nuevo.',
  apple: 'No se ha podido iniciar sesión con Apple. Inténtalo de nuevo.',
};

export function nativeSignInError(provider, raw) {
  const fallback = SOCIAL_SIGN_IN_FAILED[provider] || SOCIAL_SIGN_IN_FAILED.google;
  if (typeof raw !== 'string' || !raw.trim()) return fallback;
  if (/Debug:|^Error:|Sign In/i.test(raw)) return fallback;
  return raw;
}


// ── Apple sign-in nonce (security review r6) ─────────────────────────────────────────────────
// The page makes a random raw nonce and gives Apple its SHA-256 (hex): on the web through
// AppleID.auth.init({ nonce }), in the app through the shell's __appleSignIn(callback, { nonce }),
// which hands it to expo-apple-authentication. Apple puts that hash in the identity token's
// `nonce` claim, and the backend accepts the token only with the raw value whose hash it is, so
// a token copied off a device or out of a log cannot be replayed. The nonce is made here rather
// than in the shell because the page has Web Crypto and the shell has no crypto module, and
// because the page ships the same day while shells take days.
//
// Backward compatible both ways: a shell from before this ignores the second argument, Apple's
// token then has no nonce, and the raw one is not sent (appleNonceFor), so the backend does not
// check; with no Web Crypto, no nonce at all.
const toHex = (bytes) => Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');

export async function makeAppleNonce(cryptoObj = (typeof window !== 'undefined' ? window.crypto : undefined)) {
  try {
    if (!cryptoObj?.getRandomValues || !cryptoObj?.subtle?.digest) return null;
    const raw = toHex(cryptoObj.getRandomValues(new Uint8Array(32)));
    // raw is hex, so its UTF-8 bytes are its character codes (no TextEncoder needed).
    const digest = await cryptoObj.subtle.digest('SHA-256', Uint8Array.from(raw, (ch) => ch.charCodeAt(0)));
    return { raw, hashed: toHex(digest) };
  } catch {
    return null;
  }
}

// The claims of a JWT, unverified (the backend verifies). null when it is not one.
export function jwtClaims(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    return JSON.parse(decodeURIComponent(Array.from(atob(b64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')));
  } catch {
    return null;
  }
}

// The raw nonce to send with this token, or undefined: only when Apple put our hash in it.
export function appleNonceFor(identityToken, nonce) {
  if (!nonce?.raw || !nonce?.hashed) return undefined;
  const claim = jwtClaims(identityToken)?.nonce;
  return typeof claim === 'string' && claim.toLowerCase() === nonce.hashed ? nonce.raw : undefined;
}

/**
 * Did /auth/me say the session is over? 401 (expired, revoked, unknown account) and 403
 * (suspended, unverified) sign out. Nothing else does: a network error, a timeout, a 5xx or a
 * 429 is the server being unavailable for a moment (every deploy restarts it), not the session
 * ending, and signing out then also made the app forget the token and unlink the phone's push.
 */
export function sessionEnded(err) {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}
